import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Label, TextInput, Select, Alert } from 'flowbite-react';
import { HiOfficeBuilding } from 'react-icons/hi';
import { schoolsApi } from '../../api/schools';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';

// Nominatim response structure
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

interface AddSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (schoolId: number) => void;
  leaId: number;  // localEducationAgencyId to link school to
  leaEducationOrganizationId: number;  // For parentEducationOrganizationId
  leaName: string;  // For display purposes
}

interface SchoolFormData {
  nameOfInstitution: string;
  shortNameOfInstitution: string;
  lowGradeLevelDescriptorId: number | null;
  highGradeLevelDescriptorId: number | null;
  schoolTypeDescriptorId: number | null;
  enrollment: string;
  mainPhoneNumber: string;
  mainEmailAddress: string;
  webSite: string;
  // Address fields
  streetNumberName: string;
  city: string;
  postalCode: string;
  nameOfCounty: string;
  latitude: number | null;
  longitude: number | null;
}

const initialFormData: SchoolFormData = {
  nameOfInstitution: '',
  shortNameOfInstitution: '',
  lowGradeLevelDescriptorId: null,
  highGradeLevelDescriptorId: null,
  schoolTypeDescriptorId: null,
  enrollment: '',
  mainPhoneNumber: '',
  mainEmailAddress: '',
  webSite: '',
  streetNumberName: '',
  city: '',
  postalCode: '',
  nameOfCounty: '',
  latitude: null,
  longitude: null
};

export const AddSchoolModal: React.FC<AddSchoolModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  leaId,
  leaEducationOrganizationId,
  leaName
}) => {
  const [formData, setFormData] = useState<SchoolFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Descriptor options
  const [gradeLevelDescriptors, setGradeLevelDescriptors] = useState<Descriptor[]>([]);
  const [schoolTypeDescriptors, setSchoolTypeDescriptors] = useState<Descriptor[]>([]);
  const [loadingDescriptors, setLoadingDescriptors] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setAddressQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced Nominatim search
  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 5) { setSuggestions([]); return; }
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=us&limit=5`,
        { headers: { 'User-Agent': 'AIU-Directory/1.0 (https://aiu3.net)', 'Accept': 'application/json' } }
      );
      if (response.ok) {
        const data: NominatimResult[] = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (err) { console.error('Nominatim search error:', err); }
    finally { setSearchLoading(false); }
  }, []);

  const handleAddressInputChange = (value: string) => {
    setAddressQuery(value);
    setFormData(prev => ({ ...prev, streetNumberName: value }));
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchAddress(value), 350);
  };

  const handleSelectSuggestion = (suggestion: NominatimResult) => {
    const addr = suggestion.address;
    const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
    const city = addr.city || addr.town || addr.village || '';
    let county = addr.county || '';
    if (county.toLowerCase().endsWith(' county')) county = county.slice(0, -7);

    setFormData(prev => ({
      ...prev,
      streetNumberName: street,
      city,
      postalCode: addr.postcode || '',
      nameOfCounty: county,
      latitude: parseFloat(suggestion.lat),
      longitude: parseFloat(suggestion.lon)
    }));
    setAddressQuery(street);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Load descriptors on mount
  useEffect(() => {
    const loadDescriptors = async () => {
      setLoadingDescriptors(true);
      try {
        const [gradeLevels, schoolTypes] = await Promise.all([
          descriptorsApi.getByTypeName('Grade Level'),
          descriptorsApi.getByTypeName('School Type')
        ]);
        setGradeLevelDescriptors(gradeLevels);
        setSchoolTypeDescriptors(schoolTypes);
      } catch (err) {
        console.error('Failed to load descriptors:', err);
      } finally {
        setLoadingDescriptors(false);
      }
    };

    if (isOpen) {
      loadDescriptors();
    }
  }, [isOpen]);

  // Validate email format
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone format (US format)
  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Optional field
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 || digits.length === 11;
  };

  // Format phone number as user types
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.nameOfInstitution.trim()) {
      errors.nameOfInstitution = 'School name is required';
    }

    if (formData.mainPhoneNumber && !validatePhone(formData.mainPhoneNumber)) {
      errors.mainPhoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (formData.mainEmailAddress && !validateEmail(formData.mainEmailAddress)) {
      errors.mainEmailAddress = 'Please enter a valid email address';
    }

    if (formData.enrollment && (isNaN(Number(formData.enrollment)) || Number(formData.enrollment) < 0)) {
      errors.enrollment = 'Enrollment must be a positive number';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const schoolData = {
        nameOfInstitution: formData.nameOfInstitution.trim(),
        shortNameOfInstitution: formData.shortNameOfInstitution.trim() || undefined,
        localEducationAgencyId: leaId,
        parentEducationOrganizationId: leaEducationOrganizationId,
        lowGradeLevelDescriptorId: formData.lowGradeLevelDescriptorId || undefined,
        highGradeLevelDescriptorId: formData.highGradeLevelDescriptorId || undefined,
        schoolTypeDescriptorId: formData.schoolTypeDescriptorId || undefined,
        enrollment: formData.enrollment ? Number(formData.enrollment) : undefined,
        mainPhoneNumber: formData.mainPhoneNumber.trim() || undefined,
        mainEmailAddress: formData.mainEmailAddress.trim() || undefined,
        webSite: formData.webSite.trim() || undefined
      };

      const result = await schoolsApi.create(schoolData);

      // Create primary address if any address fields provided
      if (formData.streetNumberName.trim() || formData.city.trim()) {
        try {
          await schoolsApi.addAddress(result.schoolId, {
            streetNumberName: formData.streetNumberName.trim() || undefined,
            city: formData.city.trim() || undefined,
            postalCode: formData.postalCode.trim() || undefined,
            nameOfCounty: formData.nameOfCounty.trim() || undefined,
            latitude: formData.latitude,
            longitude: formData.longitude,
            isPrimary: true
          });
        } catch (addrErr) {
          console.warn('School created but address failed:', addrErr);
        }
      }

      onSuccess(result.schoolId);
      onClose();
    } catch (err: any) {
      let errorMessage = 'Failed to create school';

      if (err?.response?.detail) {
        errorMessage = typeof err.response.detail === 'string'
          ? err.response.detail
          : JSON.stringify(err.response.detail, null, 2);
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      console.error('Create school error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="lg">
      <div className="flex flex-col max-h-[85vh]">
        {/* Header - fixed */}
        <div className="p-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <HiOfficeBuilding className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Add School</h3>
              <p className="text-sm text-gray-500">to {leaName}</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 flex-shrink-0">
            <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
              {error}
            </Alert>
          </div>
        )}

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 space-y-4">
          {/* Required: School Name */}
          <div>
            <Label htmlFor="nameOfInstitution">
              School Name <span className="text-red-600">*</span>
            </Label>
            <TextInput
              id="nameOfInstitution"
              type="text"
              value={formData.nameOfInstitution}
              onChange={(e) => {
                setFormData({ ...formData, nameOfInstitution: e.target.value });
                if (fieldErrors.nameOfInstitution) {
                  setFieldErrors({ ...fieldErrors, nameOfInstitution: '' });
                }
              }}
              placeholder="e.g., Central Elementary School"
              className={fieldErrors.nameOfInstitution ? 'border-red-500' : ''}
              disabled={loading}
              aria-invalid={!!fieldErrors.nameOfInstitution}
            />
            {fieldErrors.nameOfInstitution && (
              <p className="text-sm text-red-600 mt-1">{fieldErrors.nameOfInstitution}</p>
            )}
          </div>

          {/* Optional: Short Name */}
          <div>
            <Label htmlFor="shortNameOfInstitution">Short Name</Label>
            <TextInput
              id="shortNameOfInstitution"
              type="text"
              value={formData.shortNameOfInstitution}
              onChange={(e) => setFormData({ ...formData, shortNameOfInstitution: e.target.value })}
              placeholder="e.g., Central ES"
              disabled={loading}
            />
          </div>

          {/* Grade Levels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lowGradeLevelDescriptorId">Lowest Grade</Label>
              <Select
                id="lowGradeLevelDescriptorId"
                value={formData.lowGradeLevelDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  lowGradeLevelDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading || loadingDescriptors}
              >
                <option value="">Select grade...</option>
                {gradeLevelDescriptors.map((descriptor) => (
                  <option key={descriptor.descriptorId} value={descriptor.descriptorId}>
                    {descriptor.shortDescription}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="highGradeLevelDescriptorId">Highest Grade</Label>
              <Select
                id="highGradeLevelDescriptorId"
                value={formData.highGradeLevelDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  highGradeLevelDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading || loadingDescriptors}
              >
                <option value="">Select grade...</option>
                {gradeLevelDescriptors.map((descriptor) => (
                  <option key={descriptor.descriptorId} value={descriptor.descriptorId}>
                    {descriptor.shortDescription}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* School Type and Enrollment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="schoolTypeDescriptorId">School Type</Label>
              <Select
                id="schoolTypeDescriptorId"
                value={formData.schoolTypeDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  schoolTypeDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading || loadingDescriptors}
              >
                <option value="">Select type...</option>
                {schoolTypeDescriptors.map((descriptor) => (
                  <option key={descriptor.descriptorId} value={descriptor.descriptorId}>
                    {descriptor.shortDescription}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="enrollment">Enrollment</Label>
              <TextInput
                id="enrollment"
                type="number"
                min="0"
                value={formData.enrollment}
                onChange={(e) => {
                  setFormData({ ...formData, enrollment: e.target.value });
                  if (fieldErrors.enrollment) {
                    setFieldErrors({ ...fieldErrors, enrollment: '' });
                  }
                }}
                placeholder="Number of students"
                className={fieldErrors.enrollment ? 'border-red-500' : ''}
                disabled={loading}
                aria-invalid={!!fieldErrors.enrollment}
              />
              {fieldErrors.enrollment && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.enrollment}</p>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mainPhoneNumber">Main Phone</Label>
              <TextInput
                id="mainPhoneNumber"
                type="tel"
                value={formData.mainPhoneNumber}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setFormData({ ...formData, mainPhoneNumber: formatted });
                  if (fieldErrors.mainPhoneNumber) {
                    setFieldErrors({ ...fieldErrors, mainPhoneNumber: '' });
                  }
                }}
                placeholder="(412) 555-1234"
                className={fieldErrors.mainPhoneNumber ? 'border-red-500' : ''}
                disabled={loading}
                aria-invalid={!!fieldErrors.mainPhoneNumber}
              />
              {fieldErrors.mainPhoneNumber && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.mainPhoneNumber}</p>
              )}
            </div>

            <div>
              <Label htmlFor="mainEmailAddress">Main Email</Label>
              <TextInput
                id="mainEmailAddress"
                type="email"
                value={formData.mainEmailAddress}
                onChange={(e) => {
                  setFormData({ ...formData, mainEmailAddress: e.target.value });
                  if (fieldErrors.mainEmailAddress) {
                    setFieldErrors({ ...fieldErrors, mainEmailAddress: '' });
                  }
                }}
                placeholder="school@district.edu"
                className={fieldErrors.mainEmailAddress ? 'border-red-500' : ''}
                disabled={loading}
                aria-invalid={!!fieldErrors.mainEmailAddress}
              />
              {fieldErrors.mainEmailAddress && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.mainEmailAddress}</p>
              )}
            </div>
          </div>

          {/* Website - Full width */}
          <div>
            <Label htmlFor="webSite">Website</Label>
            <TextInput
              id="webSite"
              type="text"
              value={formData.webSite}
              onChange={(e) => setFormData({ ...formData, webSite: e.target.value })}
              placeholder="https://school.district.edu"
              disabled={loading}
            />
          </div>

          {/* Address with Autocomplete */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">School Address</h4>
            <div className="relative" ref={suggestionsRef}>
              <Label htmlFor="streetNumberName">Street Address</Label>
              <TextInput
                id="streetNumberName"
                type="text"
                value={addressQuery}
                onChange={(e) => handleAddressInputChange(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Start typing an address..."
                disabled={loading}
                autoComplete="off"
              />
              {searchLoading && (
                <div className="absolute right-3 top-8 text-gray-400 text-xs">Searching...</div>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {suggestions.map((s) => (
                    <button
                      key={s.place_id}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      onClick={() => handleSelectSuggestion(s)}
                    >
                      <div className="text-sm text-gray-900 font-medium">
                        {[s.address.house_number, s.address.road].filter(Boolean).join(' ')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[s.address.city || s.address.town || s.address.village, s.address.state, s.address.postcode].filter(Boolean).join(', ')}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <TextInput
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Pittsburgh"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="postalCode">ZIP Code</Label>
                <TextInput
                  id="postalCode"
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="15213"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="nameOfCounty">County</Label>
                <TextInput
                  id="nameOfCounty"
                  type="text"
                  value={formData.nameOfCounty}
                  onChange={(e) => setFormData({ ...formData, nameOfCounty: e.target.value })}
                  placeholder="Allegheny"
                  disabled={loading}
                />
              </div>
            </div>
            {formData.latitude && formData.longitude && (
              <p className="text-xs text-gray-400">
                Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
              </p>
            )}
          </div>

          {/* Info Box */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <span className="font-semibold">Note:</span> After creating the school, you can add staff members and additional details from the school's profile.
            </p>
          </div>

          </div>

          {/* Action Buttons - fixed at bottom */}
          <div className="flex justify-end gap-2 p-6 pt-4 border-t flex-shrink-0">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create School'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
